/**
 * Multi-Alphabet Glyph Encoding
 *
 * Compact textual representation of arbitrary bytes using a large Unicode alphabet.
 * Currently uses CJK (U+4E00..U+9FFF) as the default encoding.
 */

// ---------------------------
// Types & Config
// ---------------------------

interface AlphabetConfig {
  name: string
  start: number
  count: number
  id: number
}

const HEADER_VERSION = 1

// ---------------------------
// Preset Alphabets
// ---------------------------

const CUNEIFORM: AlphabetConfig = {
  name: 'cuneiform',
  start: 0x12000,
  count: 1024,
  id: 1,
}

const CJK: AlphabetConfig = {
  name: 'cjk',
  start: 0x4e00,
  count: 0x9fff - 0x4e00 + 1,
  id: 2,
}

const DEFAULT_ALPHABET = CJK

// ---------------------------
// Internal utilities
// ---------------------------

function stringToBytesLatin1(s: string): Uint8Array {
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) {
    out[i] = s.charCodeAt(i) & 0xff
  }
  return out
}

function bytesToStringLatin1(u8: Uint8Array): string {
  let s = ''
  for (let i = 0; i < u8.length; i++) {
    s += String.fromCharCode(u8[i])
  }
  return s
}

function makeHeaderDigit(version: number, compressed: boolean, alphabetId: number): number {
  if (version < 0 || version > 7) {
    throw new Error('version out of range (0..7)')
  }
  if (alphabetId < 0 || alphabetId > 31) {
    throw new Error('alphabetId out of range (0..31)')
  }
  return (version & 0b111) | ((compressed ? 1 : 0) << 3) | ((alphabetId & 0b11111) << 4)
}

function parseHeaderDigit(header: number) {
  const version = header & 0b111
  const compressed = ((header >> 3) & 0b1) === 1
  const alphabetId = (header >> 4) & 0b11111
  return { version, compressed, alphabetId }
}

function bytesToBaseDigits(bytes: Uint8Array, radix: number): number[] {
  if (radix < 2) {
    throw new Error('radix must be >= 2')
  }
  if (bytes.length === 0) {
    return [0]
  }

  let src = Array.from(bytes)
  const out: number[] = []

  while (src.length > 0 && !(src.length === 1 && src[0] === 0)) {
    const quot: number[] = []
    let rem = 0
    for (let i = 0; i < src.length; i++) {
      const acc = (rem << 8) + src[i]
      const q = Math.floor(acc / radix)
      rem = acc - q * radix
      if (quot.length > 0 || q !== 0) {
        quot.push(q)
      }
    }
    out.push(rem)
    src = quot.length > 0 ? quot : [0]
  }

  if (out.length === 0) {
    out.push(0)
  }
  out.reverse()
  return out
}

function baseDigitsToBytes(digits: number[], radix: number): Uint8Array {
  if (radix < 2) {
    throw new Error('radix must be >= 2')
  }
  if (digits.length === 0) {
    return new Uint8Array([0])
  }

  let src = digits.slice()
  const out: number[] = []

  while (src.length > 0 && !(src.length === 1 && src[0] === 0)) {
    const quot: number[] = []
    let rem = 0
    for (let i = 0; i < src.length; i++) {
      const acc = rem * radix + src[i]
      const q = Math.floor(acc / 256)
      rem = acc - q * 256
      if (quot.length > 0 || q !== 0) {
        quot.push(q)
      }
    }
    out.push(rem)
    src = quot.length > 0 ? quot : [0]
  }

  if (out.length === 0) {
    out.push(0)
  }
  out.reverse()
  return new Uint8Array(out)
}

// ---------------------------
// Core encode/decode
// ---------------------------

function encodeWithAlphabet(data: string, alphabet: AlphabetConfig, compressed = false): string {
  if (!data) {
    return ''
  }

  const bytes = stringToBytesLatin1(data)
  const digits = bytesToBaseDigits(bytes, alphabet.count)

  const header = makeHeaderDigit(HEADER_VERSION, compressed, alphabet.id)
  const allDigits = [header, ...digits]

  let out = ''
  for (let i = 0; i < allDigits.length; i++) {
    const d = allDigits[i]
    if (d < 0 || d >= alphabet.count) {
      throw new Error(`digit ${d} out of range for base ${alphabet.count}`)
    }
    out += String.fromCodePoint(alphabet.start + d)
  }
  return out.normalize('NFC')
}

function decodeWithAlphabet(text: string, alphabet?: AlphabetConfig): string {
  if (!text) {
    return ''
  }
  const norm = text.normalize('NFC')

  function detectAlphabet(cp: number): AlphabetConfig {
    const candidates = [alphabet, CUNEIFORM, CJK].filter(Boolean) as AlphabetConfig[]
    for (const a of candidates) {
      if (cp >= a.start && cp < a.start + a.count) {
        return a
      }
    }
    return alphabet ?? DEFAULT_ALPHABET
  }

  const iter = norm[Symbol.iterator]()
  const first = iter.next()
  if (first.done) {
    throw new Error('Empty string')
  }
  const firstCp = first.value.codePointAt(0)!
  const detected = detectAlphabet(firstCp)

  const digits: number[] = []
  digits.push(firstCp - detected.start)
  for (let step = iter.next(); !step.done; step = iter.next()) {
    const cp = step.value.codePointAt(0)!
    const d = cp - detected.start
    if (d < 0 || d >= detected.count) {
      throw new Error(`Glyph U+${cp.toString(16).toUpperCase()} not in ${detected.name} range`)
    }
    digits.push(d)
  }

  if (digits.length === 0) {
    throw new Error('No digits found')
  }

  const header = digits[0]
  const { version, alphabetId } = parseHeaderDigit(header)
  if (version !== HEADER_VERSION) {
    throw new Error(`Unsupported version ${version}`)
  }
  if (alphabetId !== detected.id) {
    throw new Error(`Alphabet mismatch: header=${alphabetId}, expected=${detected.id} (${detected.name})`)
  }

  const payloadDigits = digits.slice(1)
  const bytes = baseDigitsToBytes(payloadDigits, detected.count)

  return bytesToStringLatin1(bytes)
}

// ---------------------------
// Public API
// ---------------------------

/**
 * Encodes data to glyphs using the default alphabet (CJK)
 */
export function dataToGlyphs(data: string, opts?: { compressed?: boolean }): string {
  return encodeWithAlphabet(data, DEFAULT_ALPHABET, opts?.compressed)
}

/**
 * Decodes glyphs back to data
 */
export function glyphsToData(text: string): string {
  return decodeWithAlphabet(text, DEFAULT_ALPHABET)
}
